import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";

interface StreamerResp {
  streamer: { overlay_key: string };
}

export default function DashboardOverlay(): JSX.Element {
  const user = useSession((s) => s.user);
  const loading = useSession((s) => s.loading);
  const q = useQuery({
    queryKey: ["overlay-key", user?.handle],
    queryFn: () => api.get<StreamerResp>(`/api/streamer/${user?.handle}`),
    enabled: !!user?.handle,
  });

  if (loading) return <div className="card">Loading…</div>;
  if (!user) return <Navigate to="/login?returnUrl=/dashboard/overlay" replace />;

  const key = q.data?.streamer.overlay_key ?? "loading…";
  const overlayUrl = `${window.location.origin}/overlay/${key}`;
  const [copied, setCopied] = useState(false);

  function copy(): void {
    navigator.clipboard.writeText(overlayUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }

  return (
    <>
      <h1>OBS Overlay</h1>

      <div className="card stack">
        <p>
          Add a <strong>Browser Source</strong> in OBS pointing at the URL below.
          1920×1080, transparent background. The totems drop on top of your stream.
        </p>
        <div className="overlay-url-row">
          <code className="overlay-url">{overlayUrl}</code>
          <button onClick={copy} className="btn btn-primary">
            {copied ? "COPIED ✓" : "COPY"}
          </button>
          <a href={overlayUrl} target="_blank" rel="noreferrer" className="btn">PREVIEW ↗</a>
        </div>
        <p style={{ color: "var(--text-mute)", fontSize: 12 }}>
          Keep this URL private. Anyone with it can drop totems on your wall.
        </p>
      </div>

      <div className="card stack">
        <h2 style={{ marginTop: 0 }}>OBS install (60 seconds)</h2>
        <ol style={{ lineHeight: 1.8 }}>
          <li>Open OBS Studio → <strong>Sources</strong> → <strong>+</strong> → <strong>Browser</strong>.</li>
          <li>Name it <code>SubsWall</code>, click OK.</li>
          <li>Paste the URL above into <strong>URL</strong>.</li>
          <li>Set width <strong>1920</strong>, height <strong>1080</strong>.</li>
          <li>Tick <strong>"Shutdown source when not visible"</strong> for a clean restart.</li>
          <li>Click OK. Test by sending a fake totem from the donor flow.</li>
        </ol>
      </div>

      <style>{`
        .overlay-url-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
        .overlay-url {
          flex: 1; min-width: 240px;
          padding: 12px 16px;
          background: var(--bg-elev, #1f1f2d);
          border: 1px solid var(--line);
          border-radius: 6px;
          font-family: var(--f-mono);
          font-size: 13px;
          word-break: break-all;
        }
      `}</style>
    </>
  );
}
